import GatewayNames from '../constant/GatewayNames';
import GatewayContainer from '../config/GatewayContainer';
import FolderClient from '../client/folder/FolderClient';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import { query } from '@trlogic/client-js';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import IEnrichmentVariableService from './IEnrichmentService';
import { BasicVariable } from '../domain/enrichment/EnrichmentDomain';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import DataType from 'constant/DataType';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import _ from 'lodash';
import Query = query.Query;
import { changeBasicVariableDirty } from '../redux/actions/BasicActions';
import VariableType from '../constant/VariableType';
import ValueFactory from '../util/ValueFactory';
import DeployErrorUtil from '../util/DeployErrorUtil';

class BasicVariableService implements IEnrichmentVariableService {
  private enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  public async save(_basicVariable: BasicVariable, isUpdate: boolean, folder: EnrichedComponentCategory) {
    const basicVariable = _.cloneDeep(_basicVariable);
    const defaultBasicVariable = ValueFactory.initializeBasicVariable();
    basicVariable.type = basicVariable.type || VariableType.BASIC;
    basicVariable.status = basicVariable.status ?? defaultBasicVariable.status;
    basicVariable.permission = basicVariable.permission || defaultBasicVariable.permission;
    basicVariable.collaborators = basicVariable.collaborators || defaultBasicVariable.collaborators;
    basicVariable.stage = basicVariable.stage || defaultBasicVariable.stage;
    basicVariable.properties = basicVariable.properties?.dataType ? basicVariable.properties : defaultBasicVariable.properties;
    if (basicVariable.name != undefined && basicVariable.name.trim().length > 0) {
      const exist = await this.exist(basicVariable.name);
      if (exist && !isUpdate) {
        MessageUtil.alreadyExist(
          <div>{LanguageUtil.get({ id: 'messages.error.variable', defaultMessage: `Variable ${basicVariable.name}` }, { 0: `${basicVariable.name}` })}</div>,
        );
        return false;
      } else {
        if (basicVariable.value != undefined) {
          const valueDataType: DataType = this.handleInput(basicVariable.value.toString());
          if (
            valueDataType != basicVariable.properties.dataType &&
            !(valueDataType == DataType.NUMBER && basicVariable.properties.dataType == DataType.STRING)
          ) {
            MessageUtil.error({
              summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),
              detail: <div>{LanguageUtil.get({ id: 'messages.cannotMatch', defaultMessage: 'Variable type and variable value type does not match' })}</div>,
            });
            return false;
          }
        }
        const dto = { id: basicVariable.name, object: basicVariable };
        await this.enrichmentClient.save(dto);
        if (!isUpdate && folder != undefined) {
          await this.folderClient.addItemToCategory(ComponentName.ENRICHMENT, folder.id, basicVariable.name);
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{basicVariable.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: `saved` })}
            </div>
          ),
        });
        dispatch(changeBasicVariableDirty(false));
        return true;
      }
    } else {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.notSave', defaultMessage: 'Variable name cannot be empty' })}</div>,
      });
      return false;
    }
  }

  public async delete(id: string) {
    if (id != undefined && id.trim().length > 0) {
      try {
        const response: any = await this.enrichmentClient.remove(id);
        if (response.code) {
          return false;
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.remove', defaultMessage: `deleted` })}
            </div>
          ),
        });
        return true;
      } catch (e: any) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }))) {
          return false;
        }
        const dependencies: any = e.response.data;
        if (dependencies?.message == 'Dependency Exception') {
          dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.BASIC_VARIABLE));
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: `can not deleted` })}
            </div>
          ),
        });
        return false;
      }
    } else {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

        detail: <div>{LanguageUtil.get({ id: 'messages.notSave', defaultMessage: 'Variable name cannot be empty' })}</div>,
      });
      return false;
    }
  }

  public async deploy(basicVariable: BasicVariable) {
    if (basicVariable.name != undefined && basicVariable.name.trim().length > 0) {
      try {
        await this.enrichmentClient.deploy(basicVariable.name);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{basicVariable.name}</b> {LanguageUtil.get({ id: 'messages.deploy', defaultMessage: `deployed` })}
            </div>
          ),
        });
      } catch (e) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }))) {
          return;
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{basicVariable.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeploy', defaultMessage: `can not deploy` })}
            </div>
          ),
        });
        console.error(e);
      }
    } else {
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

        detail: (
          <div>
            <b>{basicVariable.name}</b> {LanguageUtil.get({ id: 'messages.notSave', defaultMessage: `Variable name can not be empty` })}
          </div>
        ),
      });
    }
  }

  public async undeploy(id: string) {
    if (id != undefined && id.trim().length > 0) {
      try {
        await this.enrichmentClient.undeploy(id);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: `undeployed` })}
            </div>
          ),
        });
      } catch (e) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }))) {
          return;
        }
        console.error(e);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

          detail: (
            <div>
              <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotUndeploy', defaultMessage: `can not undeploy` })}
            </div>
          ),
        });
      }
    } else {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.basicVariable', defaultMessage: 'Basic Variable' }),

        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.notSave', defaultMessage: `Variable name can not be empty` })}
          </div>
        ),
      });
    }
  }

  public async exist(id: string) {
    if (id != undefined) {
      return await this.enrichmentClient.exists(id);
    }
    return false;
  }

  public async count(query: Query) {
    return await this.enrichmentClient.count(query);
  }

  private handleInput(input: string): DataType {
    let parsedValue: any;
    try {
      parsedValue = JSON.parse(input);
    } catch (e) {
      parsedValue = input;
    }
    if (typeof parsedValue == 'number') {
      return DataType.NUMBER;
    } else if (typeof parsedValue == 'string') {
      const dateRegexes = [
        /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|([+-]\d{2}:\d{2})))?$/,
        /^\d{1,2}\/\d{1,2}\/\d{4}$/,
        /^(?:\d{1,2}\s)?[a-zA-Z]+\s\d{1,2},?\s\d{4}$/,
        /^[a-zA-Z]{3},?\s\d{1,2}\s[a-zA-Z]{3}\s\d{4}\s\d{2}:\d{2}:\d{2}\s[+-]\d{4}$/,
      ];

      for (const regex of dateRegexes) {
        if (regex.test(parsedValue)) {
          return DataType.DATE;
        }
      }
      return DataType.STRING;
    } else if (typeof parsedValue == 'boolean') {
      return DataType.BOOLEAN;
    } else if (Array.isArray(parsedValue)) {
      return DataType.ARRAY;
    } else if (typeof parsedValue == 'object') {
      return DataType.OBJECT;
    }
    return DataType.UNKNOWN;
  }
}

type ValueType = 'number' | 'string' | 'date' | 'boolean' | 'object' | 'array' | 'unknown';
export default BasicVariableService;
